/**
 * PublicRegistration.js
 * Public registration page via invitation link, aligned with AthleteProfile.js
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner
} from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { registrationService, portalService } from '../../services/api';
import ParentSection from '../Users/ParentSection';
import AthleteMedicalCertSection from '../Portal/AthleteMedicalCertSection';
import AthleteConsentsSection from '../Portal/AthleteConsentsSection';
import AthleteCustomSectionsList from '../Portal/AthleteCustomSectionsList';
import AthleteAttachmentsList from '../Portal/AthleteAttachmentsList';
import { CERTIFICATE_TYPE_LABELS, USER_ROLES } from '../../utils/constants';
import EmailWarningModal from '../Common/EmailWarningModal';
import { checkEmailsForWarnings } from '../../utils/emailValidation';
import '../../styles/AcrosportTheme.css';
import acrosportLogo from '../../assets/acrosport-logo.png';

const PublicRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmbedded =
    new URLSearchParams(location.search).get('embedded') === 'true' ||
    (typeof window !== 'undefined' && window.self !== window.top);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    phoneNumber: '',
    address: '',
    fiscalCode: '',
    birthMunicipality: '',
    residenceMunicipality: '',
    residenceProvince: '',
    residencePostalCode: ''
  });

  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Parent state
  const [parentData, setParentData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    fiscalCode: '',
    dateOfBirth: '',
    birthMunicipality: '',
    residenceMunicipality: '',
    residenceProvince: '',
    residencePostalCode: '',
    residenceStreet: ''
  });

  // Medical certificate state
  const [certForm, setCertForm] = useState({
    certificateType: '',
    issueDate: '',
    expiryDate: ''
  });
  const [certFile, setCertFile] = useState(null);

  // Dynamic consents
  const [consents, setConsents] = useState([]);
  const [acceptedConsents, setAcceptedConsents] = useState({});

  // Custom informational sections state
  const [customSections, setCustomSections] = useState([]);
  const [readCustomSections, setReadCustomSections] = useState({});

  // Portal attachments state
  const [attachments, setAttachments] = useState([]);
  const [readAttachments, setReadAttachments] = useState({});
  const [downloadedAttachments, setDownloadedAttachments] = useState({});
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);
  const [emailWarnings, setEmailWarnings] = useState([]);
  const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);

  // Safe date parsing to avoid Safari UTC-midnight timezone drift
  const parseDateParts = (dateStr) => {
    if (!dateStr) return null;
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      const parts = dateStr.split('T')[0].split('-').map(Number);
      if (parts.length === 3 && !parts.some(isNaN)) {
        return new Date(parts[0], parts[1] - 1, parts[2]);
      }
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  const safeConvertToIso = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes('T')) return dateStr;
    const d = parseDateParts(dateStr);
    if (!d) return null;
    return `${dateStr.split('T')[0]}T00:00:00.000Z`;
  };

  // Athlete age calculation
  const getAthleteAge = () => {
    if (!formData.dateOfBirth) return null;
    const birthDate = parseDateParts(formData.dateOfBirth);
    if (!birthDate) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }
    return age;
  };

  const isMinor = () => {
    const age = getAthleteAge();
    if (age === null) return false;
    return age < 18;
  };

  const getSignatureRequirements = () => {
    const age = getAthleteAge();
    if (age === null || age >= 18) {
      return { parent: false, athlete: true };
    }
    if (age < 14) {
      return { parent: true, athlete: false };
    }
    return { parent: true, athlete: true };
  };

  // Validation flags
  const allMandatoryConsentsAccepted =
    consents.length === 0 ||
    consents.filter(c => c.isMandatory).every(c => acceptedConsents[c.id] === true);

  const allCustomSectionsRead = customSections.length === 0 || customSections.every(
    (section) => readCustomSections[section.id] === true
  );

  const allAttachmentsAccepted = attachments.length === 0 || attachments.every(
    (att) => readAttachments[att.id] === true
  );

  const isMedicalCertValid = Boolean(certForm.issueDate && certForm.expiryDate && certForm.certificateType);

  const isFormValid = () => {
    if (!formData.firstName || !formData.lastName || !formData.dateOfBirth || !formData.fiscalCode) {
      return false;
    }
    if (!isMinor()) {
      if (!formData.email || !formData.phoneNumber?.trim()) {
        return false;
      }
    }
    if (isMinor()) {
      if (
        !parentData.firstName?.trim() ||
        !parentData.lastName?.trim() ||
        !parentData.email?.trim() ||
        !parentData.phoneNumber?.trim() ||
        !parentData.fiscalCode?.trim() ||
        !parentData.dateOfBirth ||
        !parentData.birthMunicipality?.trim() ||
        !parentData.residenceStreet?.trim() ||
        !parentData.residenceMunicipality?.trim() ||
        !parentData.residenceProvince?.trim() ||
        !parentData.residencePostalCode?.trim()
      ) {
        return false;
      }
    }
    if (!allMandatoryConsentsAccepted) {
      return false;
    }
    if (!allCustomSectionsRead) {
      return false;
    }
    if (!allAttachmentsAccepted) {
      return false;
    }
    return true;
  };

  // Load public portal sections, attachments & consents
  const loadPublicData = useCallback(async () => {
    try {
      setLoading(true);
      const [sectionsRes, attachmentsRes, consentsRes] = await Promise.all([
        portalService.getPublicSections(),
        portalService.getPublicAttachments(),
        portalService.getPublicConsents()
      ]);

      if (sectionsRes.success && Array.isArray(sectionsRes.data)) {
        setCustomSections(sectionsRes.data);
      }
      if (attachmentsRes.success && Array.isArray(attachmentsRes.data)) {
        setAttachments(attachmentsRes.data);
      }
      if (consentsRes.success && Array.isArray(consentsRes.data)) {
        setConsents(consentsRes.data);
      }
    } catch (err) {
      console.warn('Errore nel caricamento delle sezioni/allegati/consensi pubblici:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPublicData();
  }, [loadPublicData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDownloadAttachmentFile = async (attachment) => {
    try {
      setDownloadingAttachmentId(attachment.id);
      const res = await portalService.downloadPublicAttachment(attachment);
      if (res.success) {
        setDownloadedAttachments(prev => ({ ...prev, [attachment.id]: true }));
      } else {
        setError(res.message || 'Errore durante il download del file');
      }
    } catch (err) {
      console.error('Download error:', err);
      setError('Errore di connessione durante il download');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const handleToggleAttachmentRead = (attachmentId, isChecked) => {
    setReadAttachments(prev => ({
      ...prev,
      [attachmentId]: isChecked
    }));
  };

  const handleToggleCustomSectionRead = (sectionId, isChecked) => {
    setReadCustomSections(prev => ({
      ...prev,
      [sectionId]: isChecked
    }));
  };

  const handleSubmit = async (e, bypassEmailWarning = false) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!isFormValid()) {
      setError('Compila tutti i campi obbligatori e accetta tutti i consensi e documenti richiesti prima di procedere.');
      return;
    }

    if (!bypassEmailWarning) {
      const fieldsToCheck = [
        {
          label: isMinor() ? 'Email Atleta (opzionale)' : 'Email Atleta',
          value: formData.email,
          fieldId: 'public-athlete-email',
          selector: 'input[name="email"]',
        },
        {
          label: 'Email Genitore',
          value: isMinor() ? parentData?.email : '',
          fieldId: 'parent-email',
        },
      ];
      const warnings = checkEmailsForWarnings(fieldsToCheck);
      if (warnings.length > 0) {
        setEmailWarnings(warnings);
        setShowEmailWarningModal(true);
        return;
      }
    }

    // Validazione facoltativa certificato medico (se compilato)
    if (certForm.issueDate || certForm.expiryDate || certFile) {
      if (!certForm.issueDate || !certForm.expiryDate) {
        setError('Per registrare il certificato medico, indica sia la data di rilascio che la data di scadenza.');
        return;
      }
      if (!certForm.certificateType) {
        setError('Per registrare il certificato medico, seleziona il tipo di certificato.');
        return;
      }
      if (new Date(certForm.expiryDate) < new Date(certForm.issueDate)) {
        setError('La data di scadenza del certificato medico non può essere precedente alla data di rilascio.');
        return;
      }
    }

    try {
      setSubmitLoading(true);
      setError('');
      setSuccess('');

      const acceptedConsentIds = Object.keys(acceptedConsents).filter(id => acceptedConsents[id]);
      const acceptedAttachmentIds = Object.keys(readAttachments).filter(id => readAttachments[id]);
      const acceptedSectionIds = Object.keys(readCustomSections).filter(id => readCustomSections[id]);
      const hasMedCert = Boolean(certForm.issueDate && certForm.expiryDate);

      const membershipConsent = consents.find(c => c.code === 'membership_request' || (c.title && c.title.toLowerCase().includes('socio')));
      const isMemberChoice = Boolean(membershipConsent && acceptedConsents[membershipConsent.id]);

      const payload = {
        ...formData,
        email: isMinor()
          ? (formData.email?.trim() || parentData.email?.trim() || '')
          : formData.email.trim(),
        phoneNumber: isMinor()
          ? (formData.phoneNumber?.trim() || null)
          : (formData.phoneNumber?.trim() || null),
        parent: isMinor() ? {
          ...parentData,
          dateOfBirth: safeConvertToIso(parentData.dateOfBirth)
        } : null,
        roles: isMemberChoice ? [USER_ROLES.ATHLETE, USER_ROLES.MEMBER] : [USER_ROLES.ATHLETE],
        isMember: isMemberChoice,
        acceptedConsentIds: acceptedConsentIds.length > 0 ? acceptedConsentIds : null,
        medicalCertificate: hasMedCert ? {
          certificateType: certForm.certificateType,
          issueDate: certForm.issueDate,
          expiryDate: certForm.expiryDate,
          notes: ''
        } : null,
        medicalCertificateFile: hasMedCert ? certFile : null,
        acceptedAttachmentIds: acceptedAttachmentIds.length > 0 ? acceptedAttachmentIds : null,
        acceptedSectionIds: acceptedSectionIds.length > 0 ? acceptedSectionIds : null
      };

      const result = await registrationService.registerPublic(payload);

      if (result.success) {
        const recipientEmail = isMinor() ? parentData.email : formData.email;
        const notice = 'Si ricorda di firmare e consegnare i documenti in segreteria.';
        const baseMsg = result.data?.message
          || result.message
          || `Registrazione avvenuta con successo! Il modulo di iscrizione in PDF e i documenti accettati sono stati inviati via email a ${recipientEmail}.`;
        const fullMsg = baseMsg.includes('firmare e consegnare')
          ? baseMsg
          : `${baseMsg} ${notice}`;
        setSuccess(fullMsg);
      } else {
        setError(result.message || 'Errore durante la registrazione. Verifica i dati inseriti e riprova.');
      }
    } catch (err) {
      setError(err.message || 'Errore di connessione al server');
      console.error('Error registering:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const formatPrintedDate = (value) => {
    if (!value) return '—';
    const d = parseDateParts(value);
    if (!d) return '—';
    return d.toLocaleDateString('it-IT');
  };

  const formatPrintedContent = (html) => {
    if (!html) return '';
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li>/gi, '• ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'")
      .replace(/[ \t]+/g, ' ')
      .replace(/\n\s*\n+/g, '\n\n')
      .trim();
  };

  const printedRegistrationDate = new Date().toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </Spinner>
          <p className="mt-3">Caricamento documentazione...</p>
        </div>
      </Container>
    );
  }

  const renderPrintDocument = () => {
    const athleteFullName = [formData.firstName, formData.lastName].filter(Boolean).join(' ') || '________________________________________';
    const parentFullName = [parentData.firstName, parentData.lastName].filter(Boolean).join(' ') || '_____________________________';
    const { parent: showParentSignature, athlete: showAthleteSignature } = getSignatureRequirements();

    return (
      <div className="registration-print-document" aria-label="Modulo di registrazione sportiva">
        <div className="print-document-header">
          <div>
            <div className="print-document-kicker">Sportus - ASD Acrosport</div>
            <h2>Modulo di Iscrizione e Tesseramento</h2>
          </div>
          <div className="print-document-date">Data: {printedRegistrationDate}</div>
        </div>

        <div className="print-document-section">
          <h3>1. Dati Anagrafici Atleta</h3>
          <div className="print-grid">
            <div className="print-field">
              <span>Nome</span>
              <strong>{formData.firstName || '—'}</strong>
            </div>
            <div className="print-field">
              <span>Cognome</span>
              <strong>{formData.lastName || '—'}</strong>
            </div>
            <div className="print-field full-width">
              <span>Codice Fiscale</span>
              <strong>{formData.fiscalCode || '—'}</strong>
            </div>
            <div className="print-field">
              <span>Data di Nascita</span>
              <strong>{formatPrintedDate(formData.dateOfBirth)}</strong>
            </div>
            <div className="print-field">
              <span>Luogo di Nascita</span>
              <strong>{formData.birthMunicipality || '—'}</strong>
            </div>
            {!isMinor() && (
              <>
                <div className="print-field">
                  <span>Telefono</span>
                  <strong>{formData.phoneNumber || '—'}</strong>
                </div>
                <div className="print-field full-width">
                  <span>Email</span>
                  <strong>{formData.email || '—'}</strong>
                </div>
              </>
            )}
            <div className="print-field full-width">
              <span>Indirizzo di Residenza</span>
              <strong>{formData.address || '—'} - {formData.residencePostalCode || ''} {formData.residenceMunicipality || ''} ({formData.residenceProvince || ''})</strong>
            </div>
          </div>
        </div>

        {isMinor() && (
          <div className="print-document-section">
            <h3>2. Dati del Genitore / Tutore Legale</h3>
            <div className="print-grid">
              <div className="print-field">
                <span>Nome</span>
                <strong>{parentData.firstName || '—'}</strong>
              </div>
              <div className="print-field">
                <span>Cognome</span>
                <strong>{parentData.lastName || '—'}</strong>
              </div>
              <div className="print-field">
                <span>Email</span>
                <strong>{parentData.email || '—'}</strong>
              </div>
              <div className="print-field">
                <span>Telefono</span>
                <strong>{parentData.phoneNumber || '—'}</strong>
              </div>
              <div className="print-field full-width">
                <span>Codice Fiscale</span>
                <strong>{parentData.fiscalCode || '—'}</strong>
              </div>
              <div className="print-field">
                <span>Data di Nascita</span>
                <strong>{formatPrintedDate(parentData.dateOfBirth)}</strong>
              </div>
              <div className="print-field">
                <span>Luogo di Nascita</span>
                <strong>{parentData.birthMunicipality || '—'}</strong>
              </div>
              <div className="print-field full-width">
                <span>Indirizzo di Residenza</span>
                <strong>{parentData.residenceStreet || '—'} - {parentData.residencePostalCode || ''} {parentData.residenceMunicipality || ''} ({parentData.residenceProvince || ''})</strong>
              </div>
            </div>
          </div>
        )}

        {isMedicalCertValid && (
          <div className="print-document-section">
            <h3>3. Certificazione Medica</h3>
            <div className="print-grid">
              <div className="print-field">
                <span>Tipo Certificato</span>
                <strong>{CERTIFICATE_TYPE_LABELS[certForm.certificateType] || certForm.certificateType}</strong>
              </div>
              <div className="print-field">
                <span>Data Rilascio</span>
                <strong>{formatPrintedDate(certForm.issueDate)}</strong>
              </div>
              <div className="print-field">
                <span>Data Scadenza</span>
                <strong>{formatPrintedDate(certForm.expiryDate)}</strong>
              </div>
            </div>
          </div>
        )}

        <div className="print-document-section">
          <h3>4. Modulo di Iscrizione, Tesseramento e Consensi</h3>
          <div className="print-grid">
            {consents.map((consent, idx) => (
              <div key={consent.id} className="print-field full-width">
                <span>{idx + 1}. {consent.title} ({consent.isMandatory ? 'Obbligatorio' : 'Facoltativo'})</span>
                {consent.description && (
                  <p className="print-consent-desc text-muted mb-1" style={{ fontSize: '0.8rem', whiteSpace: 'pre-line' }}>
                    {formatPrintedContent(consent.description)}
                  </p>
                )}
                <strong>{acceptedConsents[consent.id] ? 'AUTORIZZO ESPRESSAMENTE / ACCETTATO' : (consent.isMandatory ? 'NON ACCETTATO' : 'NON AUTORIZZO')}</strong>
              </div>
            ))}
          </div>
        </div>

        {customSections.length > 0 && (
          <div className="print-document-section">
            <h3>5. Sezioni Informative Visionate e Accettate</h3>
            <div className="print-grid">
              {customSections.map((sec, idx) => (
                <div key={sec.id} className="print-field full-width">
                  <span>{idx + 1}. {sec.title}</span>
                  {sec.content && (
                    <p className="print-consent-desc text-muted mb-1" style={{ fontSize: '0.8rem', whiteSpace: 'pre-line' }}>
                      {formatPrintedContent(sec.content)}
                    </p>
                  )}
                  <strong>{readCustomSections[sec.id] ? 'LETTO E CONFERMATO' : 'NON CONFERMATO'}</strong>
                </div>
              ))}
            </div>
          </div>
        )}

        {attachments.length > 0 && (
          <div className="print-document-section">
            <h3>6. Documenti e Allegati Ufficiali Visionati e Accettati</h3>
            <div className="print-grid">
              {attachments.map(att => (
                <div key={att.id} className="print-field full-width">
                  <span>{att.title}</span>
                  <strong>{readAttachments[att.id] ? 'ACCETTATO' : 'NON ACCETTATO'}</strong>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="print-document-footer">
          <div className="print-signature-details">
            <div><strong>Luogo e Data:</strong> Cesena, {printedRegistrationDate}</div>
            <div><strong>Nome e Cognome Tesserato/Socio:</strong> {athleteFullName}</div>
            {isMinor() && (
              <div><strong>Nome e Cognome Genitore/Tutore:</strong> {parentFullName}</div>
            )}
          </div>
          <div className="print-signatures-container">
            {showParentSignature && (
              <div className="signature-box">
                <span>Firma del Genitore / Tutore Legale</span>
                <div className="signature-line" />
              </div>
            )}
            {showAthleteSignature && (
              <div className="signature-box">
                <span>Firma dell&apos;Iscritto / Atleta</span>
                <div className="signature-line" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`acrosport-theme min-vh-100 ${isEmbedded ? 'is-embedded' : 'py-4'}`}>
      {isEmbedded && (
        <style>{`
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            overflow-x: hidden;
            background-color: transparent !important;
          }
          .public-registration-container {
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          .public-registration-embedded-card {
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 0 !important;
            background: transparent !important;
          }
          .public-registration-embedded-card > .card-body {
            padding: 0 !important;
            margin: 0 !important;
          }
        `}</style>
      )}

      <Container fluid className={`${isEmbedded ? 'p-0 m-0 w-100' : 'px-3 px-md-4'} public-registration-container`} style={!isEmbedded ? { maxWidth: '1100px' } : undefined}>
        {!isEmbedded && (
          <div className="acro-brand-header">
            <div className="acro-logo-circle">
              <img src={acrosportLogo} alt="Logo ASD Acrosport Cesena" />
            </div>
            <div className="d-block">
              <span className="acro-brand-badge">
                <i className="fas fa-shield-alt me-1" style={{ color: 'var(--acro-primary-600)' }}></i>
                Procedura Guidata &amp; Sicura
              </span>
            </div>
            <h1>Iscrizione e Tesseramento Online</h1>
            <p>
              Registrati all&apos;associazione sportiva <strong>A.S.D. Acrosport Cesena</strong> in pochi semplici passaggi.
              Compila il modulo sottostante per tesserarti o per partecipare a una lezione di prova.
            </p>
          </div>
        )}

        <Row className={isEmbedded ? "g-0 justify-content-center m-0 w-100" : "justify-content-center"}>
          <Col xs={12} className={isEmbedded ? "p-0" : ""}>
            <Card className={isEmbedded ? "public-registration-embedded-card" : "shadow-sm border-0 mb-4"}>
              {!isEmbedded && (
                <Card.Header className="bg-primary text-white py-3">
                  <h3 className="mb-0 fs-4">
                    <i className="fas fa-user-plus me-2"></i>
                    Registrazione e Tesseramento Nuovo Atleta - A.S.D. Acrosport Cesena
                  </h3>
                </Card.Header>
              )}
              <Card.Body className={isEmbedded ? "p-0 m-0" : "p-4"}>
                {error && (
                  <div className={isEmbedded ? "p-3" : ""}>
                    <Alert variant="danger" onClose={() => setError('')} dismissible>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {error}
                    </Alert>
                  </div>
                )}

                {success && (
                  <div className={`registration-success-wrapper ${isEmbedded ? 'p-3' : ''}`}>
                    <Alert variant="success" className="p-4">
                      <h4 className="alert-heading">
                        <i className="fas fa-check-circle me-2"></i>
                        Registrazione Completata!
                      </h4>
                      <p className="mb-0">{success}</p>
                    </Alert>

                    {!isEmbedded && (
                      <div className="d-flex justify-content-end gap-2 mb-3 registration-print-actions">
                        <Button variant="outline-secondary" onClick={() => navigate('/')}>
                          <i className="fas fa-home me-2"></i>
                          Torna alla Home
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {!success && (
                  <>

                    <Form onSubmit={handleSubmit} className={isEmbedded ? "w-100" : ""}>
                      {/* Dati Anagrafici Atleta */}
                      <Card className={`mb-4 shadow-sm border-0 ${isEmbedded ? 'rounded-0' : ''}`}>
                        <Card.Header className={`bg-primary text-white py-3 border-bottom ${isEmbedded ? 'rounded-0' : ''}`}>
                          <h5 className="mb-0 text-white">
                            <i className="fas fa-user me-2"></i>
                            Dati Anagrafici Atleta
                          </h5>
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Nome *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="firstName"
                                  value={formData.firstName}
                                  onChange={handleInputChange}
                                  required
                                  placeholder="Inserisci il nome"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Cognome *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="lastName"
                                  value={formData.lastName}
                                  onChange={handleInputChange}
                                  required
                                  placeholder="Inserisci il cognome"
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3" controlId="public-athlete-dob">
                                <Form.Label>Data di Nascita *</Form.Label>
                                <Form.Control
                                  type="date"
                                  name="dateOfBirth"
                                  value={formData.dateOfBirth}
                                  onChange={handleInputChange}
                                  required
                                />
                                {formData.dateOfBirth && (
                                  <Form.Text className={isMinor() ? 'text-warning fw-bold' : 'text-success'}>
                                    Età calcolata: {getAthleteAge()} anni {isMinor() ? '(Atleta Minorenne - Dati Genitore Obbligatori)' : '(Atleta Maggiorenne)'}
                                  </Form.Text>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Luogo di Nascita</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="birthMunicipality"
                                  value={formData.birthMunicipality}
                                  onChange={handleInputChange}
                                  placeholder="Es. Cesena"
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Codice Fiscale Atleta *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="fiscalCode"
                                  value={formData.fiscalCode}
                                  onChange={handleInputChange}
                                  required
                                  maxLength={16}
                                  placeholder="16 caratteri alfanumerici"
                                  style={{ textTransform: 'uppercase' }}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Indirizzo di Residenza</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="address"
                                  value={formData.address}
                                  onChange={handleInputChange}
                                  placeholder="Es. Via Garibaldi, 10"
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={5}>
                              <Form.Group className="mb-3">
                                <Form.Label>Comune di Residenza</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="residenceMunicipality"
                                  value={formData.residenceMunicipality}
                                  onChange={handleInputChange}
                                  placeholder="Es. Cesena"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-3">
                                <Form.Label>Provincia</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="residenceProvince"
                                  value={formData.residenceProvince}
                                  onChange={handleInputChange}
                                  maxLength={2}
                                  placeholder="FC"
                                  style={{ textTransform: 'uppercase' }}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group className="mb-3">
                                <Form.Label>CAP</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="residencePostalCode"
                                  value={formData.residencePostalCode}
                                  onChange={handleInputChange}
                                  maxLength={5}
                                  placeholder="47521"
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          {!isMinor() && (
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3" controlId="public-athlete-email">
                                  <Form.Label>Email Atleta *</Form.Label>
                                  <Form.Control
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required={!isMinor()}
                                    placeholder="esempio@email.it"
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Telefono *</Form.Label>
                                  <Form.Control
                                    type="tel"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleInputChange}
                                    placeholder="Inserisci il numero di telefono"
                                    required={!isMinor()}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                          )}
                        </Card.Body>
                      </Card>

                      {/* Parent Section for minors */}
                      {isMinor() && (
                        <ParentSection
                          parentData={parentData}
                          onParentChange={setParentData}
                          isRegistration={true}
                          readOnlyLocked={false}
                        />
                      )}

                      {/* Medical Certificate Section */}
                      <AthleteMedicalCertSection
                        isRegistration={true}
                        certForm={certForm}
                        setCertForm={setCertForm}
                        certFile={certFile}
                        setCertFile={setCertFile}
                        onFileValidationError={(err) => setError(err)}
                      />

                      {/* Consents Section */}
                      <AthleteConsentsSection
                        consents={consents.map(c => ({
                          ...c,
                          isAccepted: Boolean(acceptedConsents[c.id])
                        }))}
                        onToggleConsent={(consentId, isChecked) => setAcceptedConsents(prev => ({ ...prev, [consentId]: isChecked }))}
                        isRegistration={true}
                        readOnlyLocked={false}
                      />

                      {/* Custom Informational Sections */}
                      <AthleteCustomSectionsList
                        customSections={customSections}
                        readCustomSections={readCustomSections}
                        onToggleRead={handleToggleCustomSectionRead}
                        isRegistration={true}
                      />

                      {/* Allegati e Documenti Portale */}
                      <AthleteAttachmentsList
                        attachments={attachments}
                        readAttachments={readAttachments}
                        downloadedAttachments={downloadedAttachments}
                        downloadingAttachmentId={downloadingAttachmentId}
                        onDownloadAttachment={handleDownloadAttachmentFile}
                        onToggleRead={handleToggleAttachmentRead}
                        isRegistration={true}
                      />

                      {/* Submit Actions */}
                      <div className={`d-flex flex-column align-items-end mb-4 pt-3 ${isEmbedded ? 'px-3 pb-3' : ''}`}>
                        {!isFormValid() && (
                          <small className="text-danger mb-3 text-end">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            Per completare l&apos;iscrizione compila tutti i campi obbligatori e dichiara di aver letto tutti i consensi, le sezioni e i documenti allegati.
                          </small>
                        )}
                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          disabled={submitLoading || !isFormValid()}
                          className="btn-acro-primary px-5 shadow-sm"
                        >
                          {submitLoading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Salvataggio e Invio Modulo...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-check-circle me-2"></i>
                              Conferma e Salva Iscrizione
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {createPortal(renderPrintDocument(), document.body)}

      {/* Email Warning Modal */}
      <EmailWarningModal
        show={showEmailWarningModal}
        warnings={emailWarnings}
        onCorrect={() => setShowEmailWarningModal(false)}
        onIgnoreAndProceed={() => {
          setShowEmailWarningModal(false);
          handleSubmit(null, true);
        }}
      />
    </div>
  );
};

export default PublicRegistration;
